/**
 * Responsibility: say what shape the corridor takes, and how low the rail aims, in each kind of country.
 *
 * ## Why this is railway knowledge rather than biome knowledge
 *
 * Nothing about a canyon changes when a railway is laid through it. What changes is how the railway crosses
 * it — wide and benched here, a narrow blasted slot there, nothing at all on flat farmland. So the table lives
 * with the railway, and the biomes stay untouched and unaware. The flying experience is unaffected by every
 * line of this.
 *
 * ## The gorge entry, which is the one that matters
 *
 * The first attempt steered the train *into* a canyon and produced the reported artefact: seen from above, a
 * canyon running one way and a railway crossing it, a giant X. Two independent winding lines cross; steering
 * only changes how often.
 *
 * So in gorge country the rail aims far below the plateau and the corridor reaches far out, and the gorge is
 * carved *around the route*. It cannot cross itself, and the walls come up either side of a line that stays
 * roughly level — which is what the Royal Gorge looks like and what was asked for.
 *
 * ## Everything is blended by biome weight
 *
 * Values are a weighted sum over whatever biomes are present, so the corridor widens and deepens gradually as
 * the line enters gorge country and narrows again as it leaves. A per-biome switch would put a step in the
 * ground at every region boundary, which is the one thing terrain cannot have.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { RAILWAY } from '../config.js';

/**
 * Build the profile lookup.
 *
 * @param {object} options
 * @param {(x: number, z: number) => Array<{id: string, weight: number}>} options.sampleWeights - Biome weights,
 *   summing to 1.
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {{shapeAt: Function, aimBelowAt: Function, maxReach: number}}
 */
export function createCorridorProfile({ sampleWeights, tuning: overrides = RAILWAY }) {
  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  if (typeof sampleWeights !== 'function') {
    throw new Error('createCorridorProfile: sampleWeights must be a function');
  }

  /** The default, for country that asks for nothing special. */
  const ordinary = {
    reach: tuning.CORRIDOR_REACH,
    batter: tuning.CORRIDOR_BATTER,
    step: 0,
    aimBelow: 0,
  };

  /** A gorge carved along the route: wide, deep, benched. */
  const gorge = {
    reach: tuning.GORGE_REACH,
    batter: tuning.GORGE_BATTER,
    step: tuning.GORGE_STEP,
    aimBelow: tuning.GORGE_DEPTH,
  };

  /** A slot blasted through rock: as narrow as the train will fit through, crests immediately either side. */
  const rockCut = {
    reach: tuning.CUT_REACH,
    batter: tuning.CORRIDOR_BATTER,
    step: 0,
    aimBelow: 0,
  };

  /*
    Only the biomes that want something other than the default appear here. Mesa is given the rock cut but not
    the gorge: its steps are hard 120-unit tables rather than a continuous wall, so widening the corridor there
    would flatten the tables the biome exists for rather than reveal them.
  */
  const byBiome = new Map([
    ['canyon', gorge],
    ['badlands', rockCut],
    ['mesa', rockCut],
  ]);

  /** Widest the corridor can ever be, so callers can reject a coordinate before sampling anything. */
  const maxReach = Math.max(ordinary.reach, gorge.reach, rockCut.reach);

  return {
    /**
     * Corridor shape at a coordinate: how far it reaches, how it falls away, and how it benches.
     *
     * @returns {{reach: number, batter: number, step: number}}
     */
    shapeAt(x, z) {
      let reach = 0;
      let batter = 0;
      let step = 0;

      for (const { id, weight } of sampleWeights(x, z)) {
        if (weight <= 0) continue;
        const profile = byBiome.get(id) ?? ordinary;

        reach += profile.reach * weight;
        batter += profile.batter * weight;
        step += profile.step * weight;
      }

      return { reach, batter, step };
    },

    /**
     * How far below the surrounding ground the rail should aim at a coordinate, in world units.
     *
     * Zero over most of the world. Read by the surveyor rather than by the corridor, because it is a decision
     * about where the *route* goes and everything else here is about the ground beside it.
     */
    aimBelowAt(x, z) {
      let below = 0;

      for (const { id, weight } of sampleWeights(x, z)) {
        if (weight <= 0) continue;
        below += (byBiome.get(id) ?? ordinary).aimBelow * weight;
      }

      return below;
    },

    maxReach,
  };
}
